import {
  type GlobalEventsMap,
  type Method,
  type RequestPayload,
  router,
  type VisitOptions,
  type FormDataErrors,
  type FormDataKeys,
  type ErrorValue,
} from "@inertiajs/core";
import isEqual from "lodash.isequal";
import { batch, createMemo, createSignal } from "solid-js";
import { createStore, reconcile, type SetStoreFunction, unwrap } from "solid-js/store";
import { isServer } from "solid-js/web";
import { scrollToFirstFormError } from "~/util/form_errors";
import useRemember from "./useRemember";

type FormState = Record<string, unknown>;

interface InertiaFormProps<TForm extends FormState> {
  get isDirty(): boolean;

  defaults(): this;

  defaults(field: keyof TForm, value: unknown): this;

  defaults(fields: TForm): this;

  reset(...fields: string[]): this;

  transform(callback: (data: TForm) => RequestPayload): this;

  get errors(): FormDataErrors<TForm>;

  get hasErrors(): boolean;

  setError<K extends FormDataKeys<TForm>>(field: K, value: ErrorValue): void;

  setError(errors: FormDataErrors<TForm>): void;

  clearErrors(...fields: string[]): this;

  get processing(): boolean;

  get progress(): GlobalEventsMap["progress"]["parameters"][0];

  get wasSuccessful(): boolean;

  get recentlySuccessful(): boolean;

  get(url: string, options?: Partial<VisitOptions>): void;

  post(url: string, options?: Partial<VisitOptions>): void;

  put(url: string, options?: Partial<VisitOptions>): void;

  patch(url: string, options?: Partial<VisitOptions>): void;

  delete(url: string, options?: Partial<VisitOptions>): void;

  submit(method: Method, url: string, options: Partial<VisitOptions>): void;

  cancel(): void;

  data: TForm;

  setData: SetStoreFunction<TForm>;
}

function createRememberStore<TValue extends object>(
  value: TValue,
  key: string | undefined,
  keySuffix: string,
): ReturnType<typeof createStore<TValue>> {
  let restored = undefined;

  if (!isServer && key !== undefined) {
    key = `${key}:${keySuffix}`;
    restored = router.restore(key);
  }

  /* @ts-ignore */
  const [store, setStore] = createStore<TValue>(restored ?? value);

  /* @ts-ignore */
  function setStoreTrap(...args) {
    // @ts-ignore
    setStore(...args);

    if (!isServer && key !== undefined) {
      router.remember(unwrap(store), key);
    }
  }

  return [store, setStoreTrap];
}

export type InertiaForm<TForm extends FormState> = InertiaFormProps<TForm>;

export function useForm<TForm extends FormState>(initialValues?: TForm): InertiaForm<TForm>;
export function useForm<TForm extends FormState>(
  rememberKey: string,
  initialValues?: TForm,
): InertiaForm<TForm>;

export default function useForm<TForm extends FormState>(
  rememberKeyOrInitialValues?: string | TForm,
  maybeInitialValues?: TForm,
): InertiaForm<TForm> {
  const rememberKey =
    typeof rememberKeyOrInitialValues === "string" ? rememberKeyOrInitialValues : undefined;

  const [defaults, setDefaults] = createSignal<TForm>(
    // @ts-ignore
    typeof rememberKeyOrInitialValues === "string"
      ? maybeInitialValues
      : rememberKeyOrInitialValues,
  );

  const [data, setDataStore] = createRememberStore<TForm>(
    structuredClone(unwrap(defaults())),
    rememberKey,
    "data",
  );

  // Bumped on every setData call so the isDirty memo re-evaluates.
  // Solid store mutations don't change the object reference, so the memo
  // needs an explicit signal to know data changed. We use unwrap() + structuredClone
  // in the memo (not the proxy directly) because structuredClone on a store proxy throws.
  const [dataVersion, setDataVersion] = createSignal(0);

  const setData = ((...args: any[]) => {
    // @ts-ignore
    setDataStore(...args);
    setDataVersion((v) => v + 1);
  }) as SetStoreFunction<TForm>;

  const isDirty = createMemo(() => {
    dataVersion();
    const d = defaults();
    // structuredClone strips Solid's internal symbols (solid-proxy, store-node)
    // that remain after unwrap(). Without this, lodash.isEqual always reports dirty.
    return !isEqual(structuredClone(unwrap(data)), d);
  });

  const [errors, setErrors] = rememberKey
    ? useRemember<FormDataErrors<TForm>>({} as FormDataErrors<TForm>, `${rememberKey}:errors`)
    : createSignal<FormDataErrors<TForm>>({} as FormDataErrors<TForm>);
  const hasErrors = createMemo<boolean>(() => Object.keys(errors()).length > 0);

  // @ts-ignore
  let cancelToken = null;
  // @ts-ignore
  let recentlySuccessfulTimeoutId = null;
  // @ts-ignore
  let transform: (data: TForm) => RequestPayload = (data) => data;
  let defaultsCalledInOnSuccess = false;

  const [processing, setProcessing] = createSignal<boolean>(false);
  const [progress, setProgress] =
    createSignal<GlobalEventsMap["progress"]["parameters"][0]>(undefined);
  const [wasSuccessful, setWasSuccessful] = createSignal<boolean>(false);
  const [recentlySuccessful, setRecentlySuccessful] = createSignal<boolean>(false);

  const store = {
    setData,

    get data() {
      return data;
    },

    get isDirty() {
      return isDirty();
    },

    defaults(fieldOrFields?: keyof TForm | Record<keyof TForm, unknown>, maybeValue?: unknown) {
      defaultsCalledInOnSuccess = true;

      if (typeof fieldOrFields === "undefined") {
        setDefaults(() => structuredClone(unwrap(data)));
        return this;
      }

      if (typeof fieldOrFields === "string") {
        // @ts-ignore
        fieldOrFields = { [fieldOrFields]: maybeValue };
      }

      setDefaults((prev) => ({ ...(prev as object), ...(fieldOrFields as object) }) as TForm);

      return this;
    },

    reset(...fields: string[]) {
      if (fields.length === 0) {
        setData(reconcile(defaults()));

        return this;
      }

      setData(
        Object.keys(defaults())
          .filter((key) => fields.includes(key))
          .reduce((carry, key) => {
            // @ts-ignore
            carry[key] = defaults()[key];
            return carry;
          }, {}) as TForm,
      );

      return this;
    },

    transform(callback: typeof transform) {
      transform = callback;

      return this;
    },

    get errors() {
      return errors();
    },
    get hasErrors() {
      return hasErrors();
    },
    setError(fieldOrFields: keyof TForm | Record<keyof TForm, string>, maybeValue?: string) {
      const fields = (
        typeof fieldOrFields === "string" ? { [fieldOrFields]: maybeValue } : fieldOrFields
      ) as FormDataErrors<TForm>;

      // Merge into a NEW object so the (default-equality) errors signal detects the change.
      // Object.assign would mutate in place and return the same reference, which reads as no change.
      // Could also change to a store for granular updates.
      setErrors((errors) => ({ ...errors, ...fields }));

      return this;
    },
    clearErrors(...fields: string[]) {
      if (fields.length === 0) {
        setErrors(() => ({}) as FormDataErrors<TForm>);

        return this;
      }

      setErrors((errors) =>
        Object.keys(errors).reduce(
          (carry, field) =>
            Object.assign(
              carry,
              !fields.includes(field) ? { [field]: (errors as any)[field] } : {},
            ),
          {} as FormDataErrors<TForm>,
        ),
      );

      return this;
    },

    get processing() {
      return processing();
    },
    get progress() {
      return progress();
    },
    get wasSuccessful() {
      return wasSuccessful();
    },
    get recentlySuccessful() {
      return recentlySuccessful();
    },

    get(url: string, options: Partial<VisitOptions> = {}) {
      this.submit("get", url, options);
    },
    post(url: string, options: Partial<VisitOptions> = {}) {
      this.submit("post", url, options);
    },
    put(url: string, options: Partial<VisitOptions> = {}) {
      this.submit("put", url, options);
    },
    patch(url: string, options: Partial<VisitOptions> = {}) {
      this.submit("patch", url, options);
    },
    delete(url: string, options: Partial<VisitOptions> = {}) {
      this.submit("delete", url, options);
    },
    submit(method: Method, url: string, options: Partial<VisitOptions> = {}) {
      if (isServer) return;
      defaultsCalledInOnSuccess = false;

      const store = this;

      const transformedData = transform(unwrap(data));
      const _options = {
        ...options,
        // @ts-ignore
        onCancelToken(token) {
          cancelToken = token;

          if (options.onCancelToken) {
            return options.onCancelToken(token);
          }
        },
        // @ts-ignore
        onBefore(visit) {
          batch(() => {
            setWasSuccessful(false);
            setRecentlySuccessful(false);
          });
          // @ts-ignore
          clearTimeout(recentlySuccessfulTimeoutId);

          if (options.onBefore) {
            return options.onBefore(visit);
          }
        },
        // @ts-ignore
        onStart(visit) {
          setProcessing(true);

          if (options.onStart) {
            return options.onStart(visit);
          }
        },
        // @ts-ignore
        onProgress(event) {
          setProgress(event);

          if (options.onProgress) {
            return options.onProgress(event);
          }
        },
        // @ts-ignore
        async onSuccess(page) {
          batch(() => {
            setProcessing(false);
            setProgress(undefined);
            setWasSuccessful(true);
            setRecentlySuccessful(true);

            store.clearErrors();
          });

          recentlySuccessfulTimeoutId = setTimeout(() => setRecentlySuccessful(false), 2000);

          const onSuccess = options.onSuccess ? await options.onSuccess(page) : null;

          if (!defaultsCalledInOnSuccess) {
            setDefaults(() => structuredClone(unwrap(data)) as TForm);
          }

          return onSuccess;
        },
        // @ts-ignore
        onError(errors) {
          batch(() => {
            setProcessing(false);
            setProgress(undefined);

            store.clearErrors().setError(errors);
          });

          // submits preserve scroll, so a rejected save leaves the page where it was and an
          // off-screen error reads as a no-op. rAF to land after Inertia's own scroll handling.
          if (!isServer) {
            // first marker on the page, so a page still holding a sibling form's errors can land on
            // the wrong one — scoping to the submitted form needs plumbing we don't have yet
            requestAnimationFrame(() => scrollToFirstFormError(document.body));
          }

          if (options.onError) {
            return options.onError(errors);
          }
        },
        onCancel() {
          batch(() => {
            setProcessing(false);
            setProgress(undefined);
          });

          if (options.onCancel) {
            return options.onCancel();
          }
        },
        // @ts-ignore
        onFinish(visit) {
          batch(() => {
            setProcessing(false);
            setProgress(undefined);
          });
          cancelToken = null;

          if (options.onFinish) {
            return options.onFinish(visit);
          }
        },
      };

      if (method === "delete") {
        router.delete(url, { ..._options, data: transformedData });
      } else {
        router[method](url, transformedData, _options);
      }
    },

    cancel() {
      // @ts-ignore
      if (cancelToken) {
        cancelToken.cancel();
      }
    },
  };

  const proxy = new Proxy(store, {
    get(target, property) {
      if (property in target) {
        // @ts-ignore
        return target[property];
      }

      // @ts-ignore
      return data[property];
    },
  });

  // @ts-ignore
  return proxy;
}
